import React, { useEffect, useState } from 'react';
import {
  collection, doc, onSnapshot, runTransaction, serverTimestamp, updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useLang } from '../LangContext';
import { AccessDenied, canAccess } from '../adminAccess';
import Table from '../components/Table';
import { DateTime } from '../components/AdminUI';

const labelsByLang = {
  en: {
    title: 'Promo Codes',
    description: 'Discount codes customers can enter on the order review step. No usage limit and no expiry by default — a code stays active until deactivated here.',
    code: 'Code',
    codePlaceholder: 'e.g. SUMMER17',
    type: 'Discount type',
    percent: 'Percentage',
    fixed: 'Fixed amount (AZN)',
    value: 'Value',
    create: 'Create code',
    creating: 'Creating…',
    discount: 'Discount',
    status: 'Status',
    created: 'Created',
    active: 'Active',
    inactive: 'Deactivated',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    noData: 'No promo codes yet',
    errCodeRequired: 'Enter a code.',
    errValueInvalid: 'Enter a value greater than 0.',
    errExists: 'That code already exists.',
    confirmDeactivate: 'Deactivate this code? Customers will no longer be able to apply it.',
  },
  az: {
    title: 'Promo Kodları',
    description: 'Müştərilərin sifarişin nəzərdən keçirilməsi addımında daxil edə biləcəyi endirim kodları. Standart olaraq istifadə limiti və bitmə tarixi yoxdur — kod burada deaktiv edilənə qədər aktiv qalır.',
    code: 'Kod',
    codePlaceholder: 'məs. SUMMER17',
    type: 'Endirim növü',
    percent: 'Faiz',
    fixed: 'Sabit məbləğ (AZN)',
    value: 'Dəyər',
    create: 'Kod yarat',
    creating: 'Yaradılır…',
    discount: 'Endirim',
    status: 'Status',
    created: 'Yaradılıb',
    active: 'Aktiv',
    inactive: 'Deaktiv',
    deactivate: 'Deaktiv et',
    reactivate: 'Aktivləşdir',
    noData: 'Hələ promo kod yoxdur',
    errCodeRequired: 'Kod daxil edin.',
    errValueInvalid: '0-dan böyük bir dəyər daxil edin.',
    errExists: 'Bu kod artıq mövcuddur.',
    confirmDeactivate: 'Bu kod deaktiv edilsin? Müştərilər artıq onu tətbiq edə bilməyəcək.',
  },
  ru: {
    title: 'Промокоды',
    description: 'Коды скидок, которые клиенты могут ввести на шаге просмотра заказа. По умолчанию нет лимита использований и срока действия — код остаётся активным, пока не будет деактивирован здесь.',
    code: 'Код',
    codePlaceholder: 'напр. SUMMER17',
    type: 'Тип скидки',
    percent: 'Процент',
    fixed: 'Фиксированная сумма (AZN)',
    value: 'Значение',
    create: 'Создать код',
    creating: 'Создание…',
    discount: 'Скидка',
    status: 'Статус',
    created: 'Создан',
    active: 'Активен',
    inactive: 'Деактивирован',
    deactivate: 'Деактивировать',
    reactivate: 'Активировать',
    noData: 'Промокодов пока нет',
    errCodeRequired: 'Введите код.',
    errValueInvalid: 'Введите значение больше 0.',
    errExists: 'Этот код уже существует.',
    confirmDeactivate: 'Деактивировать этот код? Клиенты больше не смогут его применить.',
  },
};

/** Uppercased, trimmed lookup key — must match functions/promo-code.js's normalizePromoCode. */
function normalizeCode(raw) {
  return String(raw || '').trim().toUpperCase();
}

export default function PromoCodesPage({ user }) {
  const { lang } = useLang();
  const labels = labelsByLang[lang] || labelsByLang.en;

  const [codes, setCodes] = useState([]);
  const [codeInput, setCodeInput] = useState('');
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'promo_codes'),
      snap => setCodes(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)),
      ),
      () => setError('Unable to load promo codes. Please refresh or sign in again.'),
    );
    return () => unsub();
  }, []);

  if (!canAccess(user, 'promoCodes')) return <AccessDenied />;

  async function createCode(e) {
    e.preventDefault();
    setError('');
    const code = normalizeCode(codeInput);
    const value = Number(discountValue);

    if (!code) { setError(labels.errCodeRequired); return; }
    if (!Number.isFinite(value) || value <= 0) { setError(labels.errValueInvalid); return; }
    // A cheap pre-check against the local snapshot for fast, friendly
    // feedback -- NOT the actual guard. Two admins creating the same code
    // within the same instant could both pass this (Codex finding); the
    // transaction below is what actually prevents one from silently
    // clobbering the other's code (or reactivating one just deactivated).
    if (codes.some(c => c.id === code)) { setError(labels.errExists); return; }

    setCreating(true);
    try {
      const ref = doc(db, 'promo_codes', code);
      await runTransaction(db, async tx => {
        const existing = await tx.get(ref);
        if (existing.exists()) {
          throw new Error(labels.errExists);
        }
        tx.set(ref, {
          discountType,
          discountValue: value,
          active: true,
          createdAt: serverTimestamp(),
          createdBy: user?.email || '',
          updatedAt: serverTimestamp(),
          deactivatedAt: null,
          deactivatedBy: null,
        });
      });
      setCodeInput('');
      setDiscountValue('');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(promo) {
    const willDeactivate = promo.active === true;
    if (willDeactivate && !window.confirm(labels.confirmDeactivate)) return;
    try {
      await updateDoc(doc(db, 'promo_codes', promo.id), {
        active: !willDeactivate,
        updatedAt: serverTimestamp(),
        deactivatedAt: willDeactivate ? serverTimestamp() : null,
        deactivatedBy: willDeactivate ? (user?.email || '') : null,
      });
    } catch (err) {
      setError(err.message || String(err));
    }
  }

  return (
    <div className="page-shell">
      <div className="mb-6">
        <h1 className="page-title">{labels.title}</h1>
        <p className="page-description max-w-2xl">{labels.description}</p>
      </div>

      <form onSubmit={createCode} className="rounded-xl border border-gray-100 bg-white p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.code}</label>
          <input
            className="input-field w-full"
            value={codeInput}
            onChange={e => setCodeInput(e.target.value)}
            placeholder={labels.codePlaceholder}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.type}</label>
          <select
            className="input-field"
            value={discountType}
            onChange={e => setDiscountType(e.target.value)}
          >
            <option value="percent">{labels.percent}</option>
            <option value="fixed">{labels.fixed}</option>
          </select>
        </div>
        <div className="w-32">
          <label className="block text-xs font-semibold text-gray-500 mb-1">{labels.value}</label>
          <input
            className="input-field w-full"
            type="number"
            min="0"
            step="0.01"
            value={discountValue}
            onChange={e => setDiscountValue(e.target.value)}
            placeholder={discountType === 'percent' ? '17' : '10'}
          />
        </div>
        <button type="submit" disabled={creating} className="btn-primary whitespace-nowrap">
          {creating ? labels.creating : labels.create}
        </button>
      </form>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <Table
        headers={[labels.code, labels.discount, labels.status, labels.created, '']}
        empty={labels.noData}
      >
        {codes.map(promo => {
          // Strict === true, matching validatePromoCode's server-side check
          // exactly (Codex finding: `!== false` would show a malformed doc
          // -- missing `active`, or a stray string/number instead of a
          // boolean -- as Active here while the server correctly rejects
          // it, which is actively misleading for an admin diagnosing "why
          // doesn't this code work").
          const isActive = promo.active === true;
          return (
            <tr key={promo.id} className="border-b border-gray-50 hover:bg-gray-50 text-sm align-top">
              <td className="px-4 py-3 font-mono font-semibold">{promo.id}</td>
              <td className="px-4 py-3">
                {promo.discountType === 'fixed'
                  ? `${promo.discountValue} ₼`
                  : `${promo.discountValue}%`}
              </td>
              <td className="px-4 py-3">
                <span className={`badge ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {isActive ? labels.active : labels.inactive}
                </span>
              </td>
              <td className="px-4 py-3"><DateTime value={promo.createdAt} /></td>
              <td className="px-4 py-3">
                <button
                  onClick={() => toggleActive(promo)}
                  className={`text-xs font-semibold ${isActive ? 'text-red-600 hover:text-red-700' : 'text-emerald-600 hover:text-emerald-700'}`}
                >
                  {isActive ? labels.deactivate : labels.reactivate}
                </button>
              </td>
            </tr>
          );
        })}
      </Table>
    </div>
  );
}
